import React, { useEffect, useState, useMemo, useCallback } from "react";
import {
  ColumnDef,
  SortingState,
  VisibilityState,
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getSortedRowModel,
  useReactTable,
} from "@tanstack/react-table";
import { Button } from "@/components/ui/button";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { ScrollArea, ScrollBar } from "@/components/ui/scroll-area";
import { PlusIcon, Search, ChevronLeft, ChevronRight } from "lucide-react";
import {
  useGetAllFacilityBookingsQuery,
  useGetFacilitiesQuery,
  useLazyGetAllFacilityBookingsQuery,
} from "@/services/facilityApis";
import { useGetOrganizationQuery } from "@/services/organizationApi";
import usePagination from "@/hooks/use-pagination";
import Pagination from "@/components/ui/table/pagination-table";
import useTableData from "@/components/ui/table/data-table-logic";
import { DataTableColumnHeader } from "@/components/ui/table/data-table-column-header";
import TableFilters from "@/components/ui/table/data-table-filter";
import {
  convert24HourTo12Hour,
  displayDate,
  displayDateTime,
  displayTime,
  displayValue,
  formatDate,
  formatToPKR,
} from "@/lib/helper";
import TableSearchFilter from "@/components/ui/table/data-table-search";
import { useSelector } from "react-redux";
import { RootState } from "@/redux/store";
import { DataTableRowActions } from "./data-table-row-actions";
import { FacilityBooking } from "@/types/facility/booking";
import BookingRequestDetails from "../modal/booking-details";
import { DatePickerWithRange } from "@/components/ui/date-range/date-rangePicker";
import { toast } from "@/components/ui/use-toast";
import { useDeleteFacilityBookingMutation } from "@/services/facilityApis";
import { ErrorType } from "@/types";
import {
  addDays,
  addMonths,
  addWeeks,
  endOfDay,
  endOfMonth,
  endOfWeek,
  format,
  parseISO,
  startOfDay,
  startOfMonth,
  startOfWeek,
  subDays,
  subMonths,
  addHours,
} from "date-fns";
import {
  Calendar as FullCalendar,
  type CalendarEvent,
  type CalendarViewType,
} from "@/components/ui/full-calendar/calendar";
import { ViewToggle } from "@/components/ui/full-calendar/view-toggle";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Facility } from "@/types/facility";
import { exportBookingsToPDF } from "@/lib/utils/booking-pdf-export";
import { Download } from "lucide-react";

const { VITE_VIEW_S3_URL } = import.meta.env;
interface SearchCriteriaType {
  limit: number;
  offset: number;
  sort_order: string;
  sort_key: string;
  search_key?: string;
  type: string;
  view_type?: string;
  status?: string;
  start_date?: string;
  end_date?: string;
  facility_id?: number[];
  status_id?: string;
  payment_status_id?: string;
}

const gridInitialValue: SearchCriteriaType = {
  limit: 10,
  offset: 0,
  sort_order: "desc",
  sort_key: "id",
  type: "facility_booking",
};

const calendarInitialValue: SearchCriteriaType = {
  limit: 1000,
  offset: 0,
  sort_order: "desc",
  sort_key: "id",
  type: "facility_booking",
};

// Transform API booking data to calendar events
const transformBookingToEvent = (booking: FacilityBooking): CalendarEvent => {
  let bookingDate: Date;
  try {
    if (booking.booking_date) {
      bookingDate =
        booking.booking_date instanceof Date
          ? booking.booking_date
          : new Date(booking.booking_date);

      if (isNaN(bookingDate.getTime())) {
        console.warn(
          "Invalid booking date, using current date:",
          booking.booking_date
        );
        bookingDate = new Date();
      }
    } else {
      console.warn("No booking date found, using current date");
      bookingDate = new Date();
    }
  } catch (error) {
    console.warn("Error parsing booking date:", booking.booking_date, error);
    bookingDate = new Date();
  }

  const startTime = booking.start_time
    ? parseISO(`${format(bookingDate, "yyyy-MM-dd")}T${booking.start_time}`)
    : bookingDate;

  const endTime = booking.end_time
    ? parseISO(`${format(bookingDate, "yyyy-MM-dd")}T${booking.end_time}`)
    : addHours(startTime, 1);

  const extractBaseColor = (colorString?: string): string | undefined => {
    if (!colorString) return undefined;
    const parts = colorString.split("-");
    return parts[0] || undefined;
  };

  const transformedEvent = {
    id: booking.id?.toString() || Math.random().toString(),
    title: booking.facility_name || "Facility Booking",
    start: startTime,
    end: endTime,
    color: extractBaseColor(booking.color),
    tags: [
      {
        label: booking.status?.toUpperCase() || "BOOKED",
        color: getStatusColor(booking.status),
      },
      {
        label: booking.payment_status?.toUpperCase() || "UNPAID",
        color: getPaymentColor(booking.payment_status),
      },
    ],
    description: `Booking for ${booking.facility_name}`,
    location: booking.facility_name || "Unknown Facility",
    originalData: booking,
  } as CalendarEvent;

  return transformedEvent;
};

// Get color based on booking status
const getStatusColor = (status?: string): string => {
  switch (status?.toLowerCase()) {
    case "approved":
      return "green";
    case "pending":
      return "yellow";
    case "rejected":
      return "red";
    case "confirmed":
      return "blue";
    default:
      return "gray";
  }
};

// Get color based on payment status
const getPaymentColor = (paymentStatus?: string): string => {
  switch (paymentStatus?.toLowerCase()) {
    case "paid":
      return "green";
    case "pending":
      return "orange";
    case "failed":
      return "red";
    default:
      return "gray";
  }
};

export default function ManageBookingTable() {
  const facilityBooking = (() => {
    try {
      return (
        JSON.parse(localStorage.getItem("accessLevels") as string)
          .manage_facility_bookings ?? "no_access"
      );
    } catch {
      return "no_access";
    }
  })();

  const orgId =
    (useSelector((state: RootState) => state.auth.userInfo?.user?.org_id) as number) || 0;

  // Calendar View State
  const [viewMode, setViewMode] = useState<"calendar" | "grid">("grid");
  const [calendarViewType, setCalendarViewType] = useState<CalendarViewType>("month");
  const [currentDate, setCurrentDate] = useState<Date>(new Date());

  // Calendar date range state (separate from searchCriteria to handle navigation)
  const initialMonthStart = format(startOfMonth(new Date()), "yyyy-MM-dd");
  const initialMonthEnd = format(endOfMonth(new Date()), "yyyy-MM-dd");

  const [calendarDateRange, setCalendarDateRange] = useState<{
    start_date: string;
    end_date: string;
  }>({
    start_date: initialMonthStart,
    end_date: initialMonthEnd,
  });

  // Separate search criteria for calendar and grid views
  // Initialize calendar with proper date range
  const [gridSearchCriteria, setGridSearchCriteria] = useState<SearchCriteriaType>(gridInitialValue);
  const [calendarSearchCriteria, setCalendarSearchCriteria] = useState<SearchCriteriaType>({
    ...calendarInitialValue,
    start_date: initialMonthStart,
    end_date: initialMonthEnd,
  });

  // Use appropriate search criteria based on view mode
  const currentSearchCriteria = viewMode === "calendar" ? calendarSearchCriteria : gridSearchCriteria;
  const setCurrentSearchCriteria = viewMode === "calendar" ? setCalendarSearchCriteria : setGridSearchCriteria;

  const {
    searchCriteria: gridSearchCriteriaFromHook,
    query: gridQuery,
    toggleSortOrder,
    setSearchCriteria: setGridSearchCriteriaFromHook,
    inputValue,
    setInputValue,
  } = useTableData(gridInitialValue as any);

  // Sync grid search criteria with hook - but only when in grid view to avoid conflicts
  useEffect(() => {
    if (viewMode === "grid") {
      // Only sync if the hook has valid data (not empty)
      if (gridSearchCriteriaFromHook && Object.keys(gridSearchCriteriaFromHook).length > 0) {
        const synced = { ...gridSearchCriteriaFromHook, type: "facility_booking" } as SearchCriteriaType;
        // Only update if there are actual differences to avoid infinite loops
        if (JSON.stringify(synced) !== JSON.stringify(gridSearchCriteria)) {
          setGridSearchCriteria(synced);
        }
      }
    }
  }, [gridSearchCriteriaFromHook, viewMode]);


  // Build unified query string based on view mode
  const unifiedQuery = React.useMemo(() => {
    if (viewMode === "grid") {
      // Grid view: build query from current gridSearchCriteria state
      // This ensures filters are always included and query is always in sync
      const params = new URLSearchParams();
      const criteria = gridSearchCriteria;

      // Add required params
      params.append("limit", (criteria.limit || gridInitialValue.limit).toString());
      params.append("offset", (criteria.offset || gridInitialValue.offset).toString());
      params.append("sort_key", criteria.sort_key || gridInitialValue.sort_key);
      params.append("sort_order", criteria.sort_order || gridInitialValue.sort_order);
      params.append("type", "facility_booking");
      params.append("view_type", "admin");

      // Add optional search
      if (criteria.search_key) {
        params.append("search_key", criteria.search_key);
      }

      // Add filters - use facility_ids (plural) for API
      const facilityId = criteria.facility_id as number[] | number | undefined;
      if (facilityId !== undefined && facilityId !== null) {
        if (Array.isArray(facilityId)) {
          facilityId.forEach((id: number | string) => {
            params.append("facility_ids", String(id));
          });
        } else {
          params.append("facility_ids", String(facilityId));
        }
      }

      if (criteria.status_id) {
        params.append("status_id", String(criteria.status_id));
      }

      if (criteria.payment_status_id) {
        params.append("payment_status_id", String(criteria.payment_status_id));
      }

      if (criteria.start_date) {
        params.append("start_date", criteria.start_date);
      }

      if (criteria.end_date) {
        params.append("end_date", criteria.end_date);
      }

      return params.toString();
    } else {
      // Calendar view: build query with date range + filters
      const params = new URLSearchParams();
      params.append("view_type", "admin");

      // Use date range from searchCriteria if available (from filter), otherwise use calendar navigation date range
      const criteria = calendarSearchCriteria as any;
      const startDate = criteria.start_date || calendarDateRange.start_date;
      const endDate = criteria.end_date || calendarDateRange.end_date;

      // Ensure we have date range before making API call
      if (!startDate || !endDate) {
        return "";
      }

      params.append("start_date", startDate);
      params.append("end_date", endDate);

      // Apply filters from searchCriteria
      const facilityIds = criteria.facility_id;
      if (facilityIds !== undefined && facilityIds !== null) {
        if (Array.isArray(facilityIds)) {
          facilityIds.forEach((id: number | string) => {
            params.append("facility_ids", id.toString());
          });
        } else {
          params.append("facility_ids", facilityIds.toString());
        }
      }

      const statusId = criteria.status_id;
      if (statusId !== undefined && statusId !== null && statusId !== "") {
        params.append("status_id", statusId.toString());
      }

      const paymentStatusId = criteria.payment_status_id;
      if (paymentStatusId !== undefined && paymentStatusId !== null && paymentStatusId !== "") {
        params.append("payment_status_id", paymentStatusId.toString());
      }

      return params.toString();
    }
  }, [viewMode, calendarDateRange, calendarSearchCriteria, gridSearchCriteria]);

  const [deleteBooking, { isLoading: deleteLoading }] =
    useDeleteFacilityBookingMutation();

  // Only make API call when we have a valid query with required params
  const shouldSkipQuery = React.useMemo(() => {
    if (!orgId) return true;
    if (!unifiedQuery || unifiedQuery.trim() === "") return true;

    // For grid view, ensure we have basic params (limit, offset, sort_key, sort_order, type, view_type)
    if (viewMode === "grid") {
      const hasRequiredParams =
        unifiedQuery.includes("limit=") &&
        unifiedQuery.includes("offset=") &&
        unifiedQuery.includes("sort_key=") &&
        unifiedQuery.includes("sort_order=") &&
        unifiedQuery.includes("type=") &&
        unifiedQuery.includes("view_type=");
      return !hasRequiredParams;
    }

    // For calendar view, ensure we have date range and view_type
    if (viewMode === "calendar") {
      const hasRequiredParams =
        unifiedQuery.includes("start_date=") &&
        unifiedQuery.includes("end_date=") &&
        unifiedQuery.includes("view_type=");
      return !hasRequiredParams;
    }

    return false;
  }, [orgId, unifiedQuery, viewMode]);

  const {
    data: bookingData,
    isLoading,
    refetch,
  } = useGetAllFacilityBookingsQuery(
    {
      query: unifiedQuery,
      orgId: orgId,
    },
    {
      skip: shouldSkipQuery,
    }
  );

  // Transform calendar events from bookings
  const calendarEvents = React.useMemo(() => {
    if (viewMode !== "calendar" || !bookingData?.data) return [];
    return bookingData.data.map(transformBookingToEvent);
  }, [bookingData, viewMode]);

  const [triggerExport, { isFetching: isExporting }] = useLazyGetAllFacilityBookingsQuery();

  const handleExportPDF = async () => {
    try {
      let reportDate = "";
      let startDateStr = "";
      let endDateStr = "";

      if (viewMode === "grid") {
        startDateStr = gridSearchCriteria.start_date || "";
        endDateStr = gridSearchCriteria.end_date || "";
      } else {
        const criteria = calendarSearchCriteria;
        startDateStr = criteria.start_date || calendarDateRange.start_date;
        endDateStr = criteria.end_date || calendarDateRange.end_date;
      }

      if (startDateStr && endDateStr) {
        // helper formatDate might need import or it's just available in component if imported?
        // Check imports. formatDate is imported from date-fns? No, imports show `format` from date-fns. 
        // `formatDate` was used in line 429 of original file... `formatDate(gridSearchCriteria.start_date as string)`.
        // `formatDate` is NOT imported from date-fns usuallly, it's likely a helper or custom.
        // checking file content... `import { formatDate } from "@/lib/helper";`? No, let's check earlier view_files.
        // Line 429 original: `formatDate(gridSearchCriteria.start_date as string)`.
        // I should check imports.
        // Assuming `formatDate` is available or I use `format` from date-fns.
        // `format(new Date(str), "yyyy-MM-dd")` etc.
        // Let's safe guard and use `displayDate` from helper or just standard format.
        // The requirement is just a string. 
        // Let's use `start` and `end` directly if they are formatted strings, or `displayDate`.

        const s = displayDate(startDateStr);
        const e = displayDate(endDateStr);
        if (s === e) {
          reportDate = s;
        } else {
          reportDate = `${s} - ${e}`;
        }
      } else {
        reportDate = "All Dates";
      }

      // Build Query
      const exportParams = new URLSearchParams();
      exportParams.append("view_type", "admin");
      exportParams.append("limit", "1000");
      exportParams.append("offset", "0");
      // Always sort by date asc for valid report order
      exportParams.append("sort_key", "booking_date");
      exportParams.append("sort_order", "asc");

      if (viewMode === "grid") {
        const criteria = gridSearchCriteria;
        // sort_key and sort_order enforced above

        const facilityId = criteria.facility_id as number[] | number | undefined;
        if (facilityId !== undefined && facilityId !== null) {
          if (Array.isArray(facilityId)) {
            facilityId.forEach((id: number | string) => {
              exportParams.append("facility_ids", String(id));
            });
          } else {
            exportParams.append("facility_ids", String(facilityId));
          }
        }
        if (criteria.status_id) exportParams.append("status_id", String(criteria.status_id));
        if (criteria.payment_status_id) exportParams.append("payment_status_id", String(criteria.payment_status_id));

        if (criteria.start_date) exportParams.append("start_date", criteria.start_date);
        if (criteria.end_date) exportParams.append("end_date", criteria.end_date);
        if (criteria.search_key) exportParams.append("search_key", criteria.search_key);

      } else {
        const dateStr = format(currentDate, "yyyy-MM-dd");
        // For calendar export, we should respect the RANGE, not just currentDate single day logic from before.
        // The unifiedQuery logic correctly grabs range.

        exportParams.append("start_date", startDateStr);
        exportParams.append("end_date", endDateStr);

        const criteria = calendarSearchCriteria;
        const facilityIds = criteria.facility_id;

        // facility_id in interface is number[] only, so just check array
        if (facilityIds && Array.isArray(facilityIds) && facilityIds.length > 0) {
          facilityIds.forEach((id: number | string) => {
            exportParams.append("facility_ids", id.toString());
          });
        }

        if (criteria.status_id) exportParams.append("status_id", String(criteria.status_id));
        if (criteria.payment_status_id) exportParams.append("payment_status_id", String(criteria.payment_status_id));
      }

      const result: any = await triggerExport({
        query: exportParams.toString(),
        orgId: orgId,
      }).unwrap();

      if (result.data) {
        await exportBookingsToPDF({
          data: result.data,
          date: reportDate,
          filename: `Bookings_${reportDate.replace(/ /g, '_')}.pdf`,
        });
        toast({
          variant: "success",
          title: "Export Successful",
        });
      }

    } catch (e) {
      console.error("Export failed", e);
      toast({
        variant: "destructive",
        title: "Export Failed",
      });
    }
  };

  const showExportButton = useMemo(() => {
    return true;
  }, []);



  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isViewBookingOpen, setIsViewBookingOpen] = useState(false);
  const [action, setAction] = useState<"add" | "edit">("add");
  const [formData, setFormData] = useState<FacilityBooking | undefined>(
    undefined
  );
  const [openFilter, setOpenFilter] = useState(false);
  const [filterData, setFilterData] = useState<Record<string, any>>({});
  const [selectedEvent, setSelectedEvent] = useState<CalendarEvent | null>(null);

  const facilitiestableData = React.useMemo(() => {
    if (viewMode !== "grid" || !bookingData?.data) return [];
    return Array.isArray(bookingData?.data) ? bookingData?.data : [];
  }, [bookingData, viewMode]);

  // Update calendar date range when date or view changes
  useEffect(() => {
    if (viewMode === "calendar") {
      let start: Date, end: Date;

      if (calendarViewType === "month") {
        start = startOfMonth(currentDate);
        end = endOfMonth(currentDate);
      } else if (calendarViewType === "week") {
        start = startOfWeek(currentDate, { weekStartsOn: 1 });
        end = endOfWeek(currentDate, { weekStartsOn: 1 });
      } else {
        start = startOfDay(currentDate);
        end = endOfDay(currentDate);
      }

      const formattedStart = format(start, "yyyy-MM-dd");
      const formattedEnd = format(end, "yyyy-MM-dd");

      setCalendarDateRange({
        start_date: formattedStart,
        end_date: formattedEnd,
      });

      // Update calendar search criteria with new date range, preserving filters
      setCalendarSearchCriteria((prev) => ({
        ...prev,
        start_date: formattedStart,
        end_date: formattedEnd,
      }));
    }
  }, [currentDate, calendarViewType, viewMode]);


  // Handle Calendar Navigation
  const handlePrevious = useCallback(() => {
    setCurrentDate((prev) => {
      let newDate: Date;
      if (calendarViewType === "month") {
        newDate = subMonths(prev, 1);
      } else if (calendarViewType === "week") {
        newDate = subDays(prev, 7);
      } else {
        newDate = subDays(prev, 1);
      }
      return newDate;
    });
  }, [calendarViewType]);

  const handleNext = useCallback(() => {
    setCurrentDate((prev) => {
      let newDate: Date;
      if (calendarViewType === "month") {
        newDate = addMonths(prev, 1);
      } else if (calendarViewType === "week") {
        newDate = addDays(prev, 7);
      } else {
        newDate = addDays(prev, 1);
      }
      return newDate;
    });
  }, [calendarViewType]);

  // Handler for changing calendar view (month, week, day)
  const handleCalendarViewChange = useCallback((newView: CalendarViewType) => {
    setCalendarViewType(newView);
    // Date range will be updated by the useEffect above
  }, []);

  // View mode change handler
  const handleViewModeChange = (newViewMode: "calendar" | "grid") => {
    setViewMode(newViewMode);

    if (newViewMode === "grid") {
      // When switching to grid, preserve filters but remove date filters and ensure pagination params
      const newGridCriteria = {
        ...gridInitialValue,
        ...gridSearchCriteria,
        // Preserve filters from calendar view
        facility_id: calendarSearchCriteria.facility_id,
        status_id: calendarSearchCriteria.status_id,
        payment_status_id: calendarSearchCriteria.payment_status_id,
        // Ensure pagination params
        limit: gridSearchCriteria.limit || gridInitialValue.limit,
        offset: 0, // Reset to first page when switching views
        sort_order: gridSearchCriteria.sort_order || "desc",
        sort_key: gridSearchCriteria.sort_key || "id",
        type: "facility_booking",
      };
      // Remove date filters for grid view
      delete newGridCriteria.start_date;
      delete newGridCriteria.end_date;
      setGridSearchCriteria(newGridCriteria);
      setGridSearchCriteriaFromHook(newGridCriteria);
    } else if (newViewMode === "calendar") {
      // When switching to calendar, preserve filters and set proper date range
      const now = new Date();
      setCurrentDate(now);

      // Ensure calendar has proper date range
      let newStartDate: Date;
      let newEndDate: Date;

      if (calendarViewType === "month") {
        newStartDate = startOfMonth(now);
        newEndDate = endOfMonth(now);
      } else if (calendarViewType === "week") {
        newStartDate = startOfWeek(now, { weekStartsOn: 1 });
        newEndDate = endOfWeek(now, { weekStartsOn: 1 });
      } else {
        newStartDate = startOfDay(now);
        newEndDate = endOfDay(now);
      }

      const formattedStartDate = format(newStartDate, "yyyy-MM-dd");
      const formattedEndDate = format(newEndDate, "yyyy-MM-dd");

      setCalendarDateRange({
        start_date: formattedStartDate,
        end_date: formattedEndDate,
      });

      setCalendarSearchCriteria((prev) => ({
        ...calendarInitialValue,
        ...prev,
        // Preserve filters from grid view
        facility_id: gridSearchCriteria.facility_id,
        status_id: gridSearchCriteria.status_id,
        payment_status_id: gridSearchCriteria.payment_status_id,
        // Set date range
        start_date: formattedStartDate,
        end_date: formattedEndDate,
        offset: 0,
      }));
    }
  };

  const getDateRangeText = () => {
    if (calendarViewType === "month" || calendarViewType === "week") {
      const start = calendarViewType === "month" ? startOfMonth(currentDate) : startOfWeek(currentDate, { weekStartsOn: 1 });
      const end = calendarViewType === "month" ? endOfMonth(currentDate) : endOfWeek(currentDate, { weekStartsOn: 1 });
      return `${format(start, "dd-MM-yyyy")} to ${format(end, "dd-MM-yyyy")}`;
    } else {
      return format(currentDate, "dd-MM-yyyy");
    }
  };

  // Handle event click from calendar - view booking details
  const handleViewEvent = (event: CalendarEvent) => {
    const bookingData = event.originalData as FacilityBooking;
    if (bookingData) {
      setFormData(bookingData);
      setIsViewBookingOpen(true);
    }
  };

  // Handle edit event from calendar
  const handleEditEvent = (event: CalendarEvent) => {
    const bookingData = event.originalData as FacilityBooking;
    if (bookingData) {
      setIsDialogOpen(true);
      setAction("edit");
      setFormData(bookingData);
    }
  };

  // Handle delete event from calendar
  const handleDeleteEvent = async (event: CalendarEvent) => {
    const booking = event.originalData as FacilityBooking;
    if (!booking) return;

    try {
      const resp = await deleteBooking(booking.id as number).unwrap();
      if (resp) {
        // Handle pagination adjustment if needed (only for grid view)
        if (viewMode === "grid" && bookingData?.filtered_counts == 1 && gridSearchCriteria.offset > 0) {
          const newCriteria = {
            ...gridSearchCriteria,
            offset: gridSearchCriteria.offset - 1,
          };
          setGridSearchCriteria(newCriteria);
          setGridSearchCriteriaFromHook(newCriteria);
          setFilterData((prev) => ({ ...prev, offset: gridSearchCriteria.offset - 1 }));
        }
        setInputValue("");
        refetch();
        toast({
          variant: "success",
          title: "Booking Deleted Successfully",
        });
      }
    } catch (error) {
      console.error("Error", { error });
      if (error && typeof error === "object" && "data" in error) {
        const typedError = error as ErrorType;
        toast({
          variant: "destructive",
          title: "Error in Deletion",
          description: `${typedError.data?.detail ||
            (typedError.data as { message?: string }).message
            }`,
        });
      } else {
        toast({
          variant: "destructive",
          title: "Error in Deletion",
          description: `Something Went Wrong.`,
        });
      }
    }
  };

  const [sorting, setSorting] = useState<SortingState>([]);
  const [columnVisibility, setColumnVisibility] = useState<VisibilityState>({});
  const [rowSelection, setRowSelection] = useState({});

  const handleAddFacility = () => {
    setIsDialogOpen(true);
    setAction("add");
    setFormData(undefined);
  };

  const handleEditFacilityBooking = (data: FacilityBooking) => {
    // setIsDialogOpen(true);
    setAction("edit");
    setFormData(data);
  };

  const handleConfirmBooking = (data: FacilityBooking) => {
    setIsViewBookingOpen(true);
    setFormData(data);
  };

  const handleDeleteFacilityBooking = async (data: FacilityBooking) => {
    try {
      const resp = await deleteBooking(data.id as number).unwrap();
      if (resp) {
        if (bookingData?.filtered_counts == 1 && gridSearchCriteria.offset > 0) {
          const newCriteria = {
            ...gridSearchCriteria,
            offset: gridSearchCriteria.offset - 1,
          };
          setGridSearchCriteria(newCriteria);
          setGridSearchCriteriaFromHook(newCriteria);
          setFilterData((prev) => ({ ...prev, offset: gridSearchCriteria.offset - 1 }));
        }
        setInputValue("");
        refetch();
        toast({
          variant: "success",
          title: "Facility Deleted Successfully",
        });
      }
    } catch (error) {
      console.error("Error", { error });
      if (error && typeof error === "object" && "data" in error) {
        const typedError = error as ErrorType;
        toast({
          variant: "destructive",
          title: "Error in form Submission",
          description: `${typedError.data?.detail ||
            (typedError.data as { message?: string }).message
            }`,
        });
      } else {
        toast({
          variant: "destructive",
          title: "Error in form Submission",
          description: `Something Went Wrong.`,
        });
      }
    }
  };

  const actionsColumn: ColumnDef<FacilityBooking> = {
    id: "actions",
    header: "Actions",
    maxSize: 100,
    cell: ({ row }) => (
      <DataTableRowActions
        access={facilityBooking}
        data={row.original}
        refetch={refetch}
        handleEdit={handleEditFacilityBooking}
        handleDelete={handleDeleteFacilityBooking}
        handleConfirmBooking={handleConfirmBooking}
      />
    ),
  };

  const columns: ColumnDef<FacilityBooking>[] = [
    {
      accessorKey: "member_name",
      header: ({ column }) => (
        <DataTableColumnHeader
          column={column}
          title="Member"
          sortKey="member_name"
          sortable={false}
          searchCriteria={gridSearchCriteria}
          toggleSortOrder={toggleSortOrder}
        />
      ),
      cell: ({ row }) => {
        return (
          <div className="">
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <p className="capitalize cursor-pointer">
                    <span>
                      {displayValue(
                        `${row.original.member_name}`.length > 20
                          ? `${row.original.member_name}`.substring(0, 20) +
                          "..."
                          : `${row.original.member_name}`
                      )}
                    </span>
                  </p>
                </TooltipTrigger>
                <TooltipContent className="mr-24">
                  <p className="capitalize text-sm w-fit ">
                    {displayValue(`${row?.original.member_name}`)}
                  </p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </div>
        );
      },
      enableSorting: false,
      enableHiding: false,
    },
    {
      accessorKey: "id",
      header: ({ column }) => (
        <DataTableColumnHeader
          column={column}
          title="Facility"
          sortKey="facility_name"
          searchCriteria={gridSearchCriteria}
          toggleSortOrder={toggleSortOrder}
        />
      ),
      cell: ({ row }) => {
        return (
          <div className="">
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <p className="capitalize cursor-pointer">
                    <span>
                      {displayValue(
                        `${row.original.facility_name}`.length > 20
                          ? `${row.original.facility_name}`.substring(0, 20) +
                          "..."
                          : `${row.original.facility_name}`
                      )}
                    </span>
                  </p>
                </TooltipTrigger>
                <TooltipContent className="mr-24">
                  <p className="capitalize text-sm w-fit ">
                    {displayValue(`${row?.original.facility_name}`)}
                  </p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </div>
        );
      },
      enableSorting: false,
      enableHiding: false,
    },
    {
      accessorKey: "status_id",
      header: ({ column }) => (
        <DataTableColumnHeader
          column={column}
          title="Status"
          sortKey="status_id"
          searchCriteria={gridSearchCriteria}
          toggleSortOrder={toggleSortOrder}
        />
      ),
      cell: ({ row }) => {
        return (
          <p className="text-nowrap">{displayValue(row.original.status)}</p>
        );
      },
      enableSorting: false,
      enableHiding: false,
    },
    {
      accessorKey: "payment_status_id",
      header: ({ column }) => (
        <DataTableColumnHeader
          column={column}
          title="Payment Status"
          sortKey="payment_status_id"
          searchCriteria={gridSearchCriteria}
          toggleSortOrder={toggleSortOrder}
        />
      ),
      cell: ({ row }) => {
        return (
          <p className="text-nowrap">
            {displayValue(row.original.payment_status)}
          </p>
        );
      },
      enableSorting: false,
      enableHiding: false,
    },

    {
      accessorKey: "total",
      header: ({ column }) => (
        <DataTableColumnHeader
          column={column}
          title="Total"
          sortKey="total"
          searchCriteria={gridSearchCriteria}
          toggleSortOrder={toggleSortOrder}
        />
      ),
      cell: ({ row }) => {
        return <p>{formatToPKR(row.original.total)}</p>;
      },
      enableSorting: false,
      enableHiding: false,
    },
    {
      accessorKey: "slot_start_time",
      header: ({ column }) => (
        <DataTableColumnHeader
          column={column}
          title="Start Time"
          sortKey="slot_start_time"
          searchCriteria={gridSearchCriteria}
          toggleSortOrder={toggleSortOrder}
        />
      ),
      cell: ({ row }) => {
        const time = row.original.slot_start_time 
          ? format(new Date(row.original.slot_start_time), "hh:mm a")
          : row.original.start_time 
          ? convert24HourTo12Hour(row.original.start_time)
          : "N/A";
        return <p>{time}</p>;
      },
      enableSorting: false,
      enableHiding: false,
    },
    {
      accessorKey: "slot_end_time",
      header: ({ column }) => (
        <DataTableColumnHeader
          column={column}
          title="End Time"
          sortKey="slot_end_time"
          searchCriteria={gridSearchCriteria}
          toggleSortOrder={toggleSortOrder}
        />
      ),
      cell: ({ row }) => {
        const time = row.original.slot_end_time
          ? format(new Date(row.original.slot_end_time), "hh:mm a")
          : row.original.end_time
          ? convert24HourTo12Hour(row.original.end_time)
          : "N/A";
        return <p>{time}</p>;
      },
      enableSorting: false,
      enableHiding: false,
    },
    {
      accessorKey: "booking_date",
      header: ({ column }) => (
        <DataTableColumnHeader
          column={column}
          title="Booking Date"
          sortKey="booking_date"
          searchCriteria={gridSearchCriteria}
          toggleSortOrder={toggleSortOrder}
        />
      ),
      cell: ({ row }) => {
        return <p>{displayDate(row.original.booking_date)}</p>;
      },
      enableSorting: false,
      enableHiding: false,
    },

    ...(facilityBooking !== "read" ? [actionsColumn] : []),
  ];

  const table = useReactTable({
    data: facilitiestableData as FacilityBooking[],
    columns,
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    onColumnVisibilityChange: setColumnVisibility,
    onRowSelectionChange: setRowSelection,
    state: {
      sorting,
      columnVisibility,
      rowSelection,
    },
  });

  const totalRecords = bookingData?.filtered_counts || 0;
  const {
    handleLimitChange,
    handleNextPage,
    handlePrevPage,
    handleFirstPage,
    handleLastPage,
    isLastPage,
  } = usePagination<SearchCriteriaType>({
    totalRecords,
    searchCriteria: gridSearchCriteria,
    setSearchCriteria: ((newCriteria: any) => {
      const criteria = typeof newCriteria === 'function' ? newCriteria(gridSearchCriteria) : newCriteria;
      setGridSearchCriteria(criteria);
      setGridSearchCriteriaFromHook(criteria as any);
    }) as React.Dispatch<React.SetStateAction<SearchCriteriaType>>,
  });

  // Get facilities for filter
  const { data: facilitiesData } = useGetFacilitiesQuery(
    {
      query: "status=active&sort_key=id&sort_order=desc&bookable=true",
      orgId: orgId,
    },
    {
      skip: !orgId,
    }
  );

  const handleFilterChange = (
    field: string,
    value: string | number | number[] | React.ChangeEvent<HTMLInputElement>
  ) => {
    let newValue: string | number | (string | number)[] | undefined;

    if (Array.isArray(value)) {
      newValue = value.length > 0 ? value : undefined;
    } else if (typeof value === "string") {
      newValue = value.trim() === "" ? undefined : value;
    } else if (typeof value === "number") {
      newValue = value;
    } else {
      newValue = value.target.value.trim() === "" ? undefined : value.target.value;
    }

    setFilterData((prev) => {
      const newFilter = { ...prev };

      if (newValue === undefined || (Array.isArray(newValue) && newValue.length === 0)) {
        delete newFilter[field];
      } else {
        newFilter[field] = newValue;
      }

      return newFilter;
    });
  };

  // Filter configuration for advanced filtering
  const filterDisplay = [
    {
      type: "multiselect",
      name: "facility_id",
      label: "Facility",
      options:
        facilitiesData?.data?.map((facility: Facility) => ({
          value: facility.id,
          label: facility.name,
        })) || [],
      function: (value: (string | number)[]) => {
        const numArray = value.map(v => typeof v === 'string' ? parseInt(v, 10) : v);
        handleFilterChange("facility_id", numArray);
      },
    },
    {
      type: "select",
      name: "status_id",
      label: "Booking Status",
      options: [
        { id: "2", name: "Pending" },
        { id: "6", name: "Rejected" },
        { id: "7", name: "Approved" },
      ],
      function: (value: string) => handleFilterChange("status_id", value),
    },
    {
      type: "select",
      name: "payment_status_id",
      label: "Payment Status",
      options: [
        { id: "1", name: "Paid" },
        { id: "2", name: "Pending" },
      ],
      function: (value: string) => handleFilterChange("payment_status_id", value),
    },
  ];

  const handleDateRange = (dates: {
    start_date: Date | undefined;
    end_date: Date | undefined;
  }) => {
    const formattedStartDate = dates.start_date
      ? formatDate(dates.start_date)
      : "";
    const formattedEndDate = dates.end_date ? formatDate(dates.end_date) : "";

    setFilterData((prev) => ({
      ...prev,
      start_date: formattedStartDate,
      end_date: formattedEndDate,
    }));

    if (viewMode === "grid") {
      setGridSearchCriteria((prev: any) => ({
        ...prev,
        start_date: formattedStartDate ?? undefined,
        end_date:
          formattedStartDate && !formattedEndDate ? undefined : formattedEndDate,
        offset: 0,
        sort_key: "id",
        sort_order: "desc",
      }));
      setGridSearchCriteriaFromHook((prev: any) => ({
        ...prev,
        start_date: formattedStartDate ?? undefined,
        end_date:
          formattedStartDate && !formattedEndDate ? undefined : formattedEndDate,
        offset: 0,
        sort_key: "id",
        sort_order: "desc",
      }));
    } else {
      // For calendar view, update calendar date range
      if (formattedStartDate && formattedEndDate) {
        setCalendarDateRange({
          start_date: formattedStartDate,
          end_date: formattedEndDate,
        });
        setCalendarSearchCriteria((prev: any) => ({
          ...prev,
          start_date: formattedStartDate,
          end_date: formattedEndDate,
          offset: 0,
        }));
      }
    }
  };

  return (
    <div className="w-full space-y-4">
      <div className="flex flex-col gap-2 px-3">
        <div className="flex flex-row items-center gap-2 min-w-0">
          {/* Table Filter component */}
          <TableSearchFilter
            setSearchCriteria={(newCriteria: any) => {
              if (viewMode === "grid") {
                // Merge with existing criteria to preserve filters and pagination
                const criteria = {
                  ...gridInitialValue,
                  ...gridSearchCriteria,
                  ...newCriteria,
                  type: "facility_booking",
                  // Preserve limit if not in newCriteria
                  limit: newCriteria.limit !== undefined ? newCriteria.limit : (gridSearchCriteria.limit || gridInitialValue.limit),
                  // Preserve offset if not in newCriteria (search might reset it)
                  offset: newCriteria.offset !== undefined ? newCriteria.offset : gridSearchCriteria.offset,
                } as SearchCriteriaType;
                setGridSearchCriteria(criteria);
                setGridSearchCriteriaFromHook(criteria as any);
              } else {
                // For calendar, preserve date range
                const criteria = {
                  ...calendarInitialValue,
                  ...calendarSearchCriteria,
                  ...newCriteria,
                  type: "facility_booking",
                  // Preserve date range
                  start_date: newCriteria.start_date || calendarSearchCriteria.start_date || calendarDateRange.start_date,
                  end_date: newCriteria.end_date || calendarSearchCriteria.end_date || calendarDateRange.end_date,
                } as SearchCriteriaType;
                setCalendarSearchCriteria(criteria);
              }
            }}
            inputValue={inputValue}
            setInputValue={setInputValue}
          />
        </div>

        <div className="flex flex-row items-center justify-between gap-2 flex-wrap">
          {viewMode === "calendar" && (
            <div className="flex items-center flex-wrap gap-2">
              <Button variant="ghost" size="icon" onClick={handlePrevious} className="h-8 w-8 shrink-0">
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <span className="text-xs sm:text-sm font-medium w-auto sm:w-48 text-center">{getDateRangeText()}</span>
              <Button variant="ghost" size="icon" onClick={handleNext} className="h-8 w-8 shrink-0">
                <ChevronRight className="h-4 w-4" />
              </Button>
              <Select
                value={calendarViewType}
                onValueChange={(value) => handleCalendarViewChange(value as CalendarViewType)}
              >
                <SelectTrigger className="h-8 w-[80px] text-xs shrink-0">
                  <SelectValue placeholder="View" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="month">Month</SelectItem>
                  <SelectItem value="week">Week</SelectItem>
                  <SelectItem value="day">Day</SelectItem>
                </SelectContent>
              </Select>
            </div>
          )}

        {/* Buttons Container */}
        <div className="flex flex-row items-center gap-1.5 sm:gap-2 shrink-0 ml-auto">
          {viewMode === "grid" && (
            <>
              <div className="text-sm text-black flex items-center gap-1 h-8">
                <DatePickerWithRange
                  name={"Date Range"}
                  value={{
                    start_date: filterData.start_date,
                    end_date: filterData.end_date,
                  }}
                  onValueChange={(dates) => {
                    handleDateRange(dates);
                  }}
                  label={"Select date range"}
                  className="w-full"
                />
              </div>
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      variant={"outline"}
                      className="h-8 w-8 sm:w-auto p-0 sm:px-2 flex items-center justify-center gap-1 shrink-0"
                      onClick={() => {
                        setFilterData({});
                        const resetCriteria = { ...gridInitialValue };
                        setGridSearchCriteria(resetCriteria);
                        setGridSearchCriteriaFromHook(resetCriteria as any);
                        setInputValue("");
                      }}
                    >
                      <i className="fa-solid fa-arrows-rotate"></i>
                      <span className="hidden sm:inline">Reset</span>
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>Reset Filter</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </>
          )}

          {showExportButton && (
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="outline"
                    className="h-8 w-8 sm:w-auto p-0 sm:px-3 gap-2 border-primary bg-primary text-black hover:bg-primary/80 hover:text-white shrink-0 flex items-center justify-center"
                    onClick={handleExportPDF}
                    disabled={isExporting}
                  >
                    <Download className="h-3.5 w-3.5" />
                    <span className="hidden sm:inline">{isExporting ? "Exporting..." : "Export PDF"}</span>
                  </Button>
                </TooltipTrigger>
                <TooltipContent>
                  <p>Export PDF</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          )}

          <button
            className={`border rounded-md h-8 w-8 shrink-0 text-gray-400 flex items-center justify-center ${Object.keys(filterData).length ? "border-primary" : ""
              }`}
            onClick={() => setOpenFilter(true)}
          >
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <i
                    className={`fa fa-filter ${Object.keys(filterData).length ? "text-primary" : ""
                      }`}
                  ></i>
                </TooltipTrigger>
                <TooltipContent>
                  <p>click to apply filter</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </button>

          <ViewToggle
            view={viewMode}
            onViewChange={handleViewModeChange}
            className="h-8"
          />
        </div>
        </div>
      </div>
     {viewMode === "grid" ? (
        <div className="hidden md:block rounded-none border border-border  ">
          <ScrollArea className="w-full relative" hideScrollToTop={true}>
            <ScrollBar
              orientation="horizontal"
              className="relative z-30 cursor-grab h-2"
            />
            <Table className="w-full sm:max-h-[40vh] lg:max-h-[50vh] xl:max-h-[60vh] 2xl:max-h-[65vh]  ">
              <TableHeader className="bg-secondary">
                {table?.getHeaderGroups().map((headerGroup) => (
                  <TableRow key={headerGroup.id}>
                    {headerGroup.headers.map((header) => {
                      return (
                        <TableHead key={header.id}>
                          {header.isPlaceholder
                            ? null
                            : flexRender(
                              header.column.columnDef.header,
                              header.getContext()
                            )}
                        </TableHead>
                      );
                    })}
                  </TableRow>
                ))}
              </TableHeader>
              <TableBody>
                {isLoading ? (
                  <TableRow>
                    <TableCell
                      colSpan={columns.length}
                      className="h-24 text-center"
                    >
                      <div className="flex space-x-2 justify-center items-center bg-white ">
                        <div className="size-3 bg-black rounded-full animate-bounce [animation-delay:-0.3s]"></div>
                        <div className="size-3 bg-black rounded-full animate-bounce [animation-delay:-0.15s]"></div>
                        <div className="size-3 bg-black rounded-full animate-bounce"></div>
                      </div>
                    </TableCell>
                  </TableRow>
                ) : table.getRowModel().rows.length ? (
                  table.getRowModel().rows.map((row) => (
                    <TableRow
                      key={row.id}
                      data-state={row.getIsSelected() && "selected"}
                      onClick={(e) => {
                        if (
                          facilityBooking !== "read" &&
                          (row.original.status == "Pending" && row.original.payment_status != "Paid") &&
                          e.target instanceof Element &&
                          !e.target.closest('[role="checkbox"]') &&
                          (e.target === e.currentTarget || e.target.closest("td"))
                        ) {
                          handleEditFacilityBooking(
                            row.original as unknown as FacilityBooking
                          );
                        }
                      }}
                      className={
                        facilityBooking !== "read"
                          ? "cursor-pointer hover:bg-secondary"
                          : ""
                      }
                    >
                      {row.getVisibleCells().map((cell) => (
                        <TableCell key={cell.id}>
                          {flexRender(
                            cell.column.columnDef.cell,
                            cell.getContext()
                          )}
                        </TableCell>
                      ))}
                    </TableRow>
                  ))
                ) : facilitiestableData.length > 0 ? (
                  <TableRow className="!p-0">
                    <TableCell
                      colSpan={columns.length}
                      className="h-24 text-center  "
                    >
                      No facility found.
                    </TableCell>
                  </TableRow>
                ) : (
                  <TableRow>
                    <TableCell
                      colSpan={columns.length}
                      className="h-24 text-center"
                    >
                      No records found.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </ScrollArea>
        </div>
      ) : null}

      {viewMode === "grid" && (
        <div className="block md:hidden space-y-3 px-3">
          {isLoading ? (
            <div className="flex space-x-2 justify-center items-center py-10">
              <div className="size-3 bg-black rounded-full animate-bounce [animation-delay:-0.3s]"></div>
              <div className="size-3 bg-black rounded-full animate-bounce [animation-delay:-0.15s]"></div>
              <div className="size-3 bg-black rounded-full animate-bounce"></div>
            </div>
          ) : facilitiestableData.length ? (
            facilitiestableData.map((booking: FacilityBooking) => (
              <div
                key={booking.id}
                className="border border-border rounded-md p-3 space-y-2 bg-white"
                onClick={() => {
                  if (
                    facilityBooking !== "read" &&
                    booking.status == "Pending" &&
                    booking.payment_status != "Paid"
                  ) {
                    handleEditFacilityBooking(booking);
                  }
                }}
              >
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <p className="font-medium text-sm capitalize truncate">
                      {displayValue(`${booking.member_name}`)}
                    </p>
                    <p className="text-xs text-gray-500 capitalize truncate">
                      {displayValue(`${booking.facility_name}`)}
                    </p>
                  </div>
                  <div
                    className="shrink-0"
                    onClick={(e) => e.stopPropagation()}
                  >
                    {facilityBooking !== "read" && (
                      <DataTableRowActions
                        access={facilityBooking}
                        data={booking}
                        refetch={refetch}
                        handleEdit={handleEditFacilityBooking}
                        handleDelete={handleDeleteFacilityBooking}
                        handleConfirmBooking={handleConfirmBooking}
                      />
                    )}
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-y-1 text-xs">
                  <div className="text-gray-500">Status</div>
                  <div className="text-right">{displayValue(booking.status)}</div>

                  <div className="text-gray-500">Payment Status</div>
                  <div className="text-right">{displayValue(booking.payment_status)}</div>

                  <div className="text-gray-500">Total</div>
                  <div className="text-right">{formatToPKR(booking.total)}</div>

                  <div className="text-gray-500">Start Time</div>
                  <div className="text-right">{convert24HourTo12Hour(booking.start_time)}</div>

                  <div className="text-gray-500">End Time</div>
                  <div className="text-right">{convert24HourTo12Hour(booking.end_time)}</div>

                  <div className="text-gray-500">Booking Date</div>
                  <div className="text-right">{displayDate(booking.booking_date)}</div>
                </div>
              </div>
            ))
          ) : (
            <div className="h-24 flex items-center justify-center text-sm text-gray-500">
              No records found.
            </div>
          )}
        </div>
      )}

      {viewMode === "calendar" && (
        <div className="h-[70vh] sm:h-screen overflow-x-auto">
          {isLoading ? (
            <div className="flex justify-center items-center h-full">
              <div className="flex space-x-2">
                <div className="size-4 bg-primary rounded-full animate-bounce [animation-delay:-0.3s]"></div>
                <div className="size-4 bg-primary rounded-full animate-bounce [animation-delay:-0.15s]"></div>
                <div className="size-4 bg-primary rounded-full animate-bounce"></div>
              </div>
            </div>
          ) : (
            <FullCalendar
              events={calendarEvents}
              initialView={calendarViewType}
              initialDate={currentDate}
              key={`calendar-${currentDate.getTime()}-${calendarViewType}`}
              onCreateEvent={handleAddFacility}
              onViewEvent={handleViewEvent}
              // onEditEvent={handleEditEvent}
              // onDeleteEvent={handleDeleteEvent}
              access={facilityBooking}
              isCustomView={true}
              disableNavigation={true}
              hideEventActions={false}
            />
          )}
        </div>
      )}

      {/* pagination */}
      {facilitiestableData && facilitiestableData.length > 0 && viewMode === "grid" && (
        <Pagination
          limit={gridSearchCriteria.limit}
          offset={gridSearchCriteria.offset}
          totalItems={totalRecords}
          onLimitChange={handleLimitChange}
          onNextPage={handleNextPage}
          onPrevPage={handlePrevPage}
          onFirstPage={handleFirstPage}
          onLastPage={handleLastPage}
          isLastPage={isLastPage}
        />
      )}

      <TableFilters
        isOpen={openFilter}
        setOpen={setOpenFilter}
        initialValue={viewMode === "calendar" ? calendarInitialValue as any : gridInitialValue as any}
        filterData={filterData}
        setFilter={setFilterData}
        setSearchCriteria={(newCriteriaOrFunction: any) => {
          // Handle both function and direct value forms
          const getNewCriteria = () => {
            if (typeof newCriteriaOrFunction === 'function') {
              // If it's a function, call it with the current state
              if (viewMode === "grid") {
                return newCriteriaOrFunction(gridSearchCriteria);
              } else {
                return newCriteriaOrFunction(calendarSearchCriteria);
              }
            }
            return newCriteriaOrFunction;
          };

          const newCriteria = getNewCriteria();

          if (viewMode === "grid") {
            // For grid view, merge with existing criteria and ensure limit/offset are preserved
            const criteria = {
              ...gridInitialValue,
              ...gridSearchCriteria,
              ...newCriteria,
              type: "facility_booking",
              // Always ensure limit is set
              limit: newCriteria.limit !== undefined ? newCriteria.limit : (gridSearchCriteria.limit || gridInitialValue.limit),
              // offset is set to 0 by TableFilters when applying filters, which is correct
              offset: newCriteria.offset !== undefined ? newCriteria.offset : 0,
              // Ensure sort params are set
              sort_key: newCriteria.sort_key || gridSearchCriteria.sort_key || gridInitialValue.sort_key,
              sort_order: newCriteria.sort_order || gridSearchCriteria.sort_order || gridInitialValue.sort_order,
            } as SearchCriteriaType;

            // Clean up undefined/null values but keep essential fields
            const cleanedCriteria = { ...criteria };
            Object.keys(cleanedCriteria).forEach(key => {
              const value = cleanedCriteria[key as keyof typeof cleanedCriteria];
              // Keep limit, offset, type, sort_key, sort_order, and search_key
              if (key !== 'limit' && key !== 'offset' && key !== 'type' && key !== 'sort_key' && key !== 'sort_order' && key !== 'search_key') {
                if (value === undefined || value === null || value === '') {
                  delete cleanedCriteria[key as keyof typeof cleanedCriteria];
                }
                // Remove empty arrays
                if (Array.isArray(value) && value.length === 0) {
                  delete cleanedCriteria[key as keyof typeof cleanedCriteria];
                }
              }
            });

            setGridSearchCriteria(cleanedCriteria);
            setGridSearchCriteriaFromHook(cleanedCriteria as any);
          } else {
            // For calendar view, merge with existing criteria and preserve date range
            const criteria = {
              ...calendarInitialValue,
              ...calendarSearchCriteria,
              ...newCriteria,
              type: "facility_booking",
              // Preserve date range from calendar navigation if not in newCriteria
              start_date: newCriteria.start_date || calendarSearchCriteria.start_date || calendarDateRange.start_date,
              end_date: newCriteria.end_date || calendarSearchCriteria.end_date || calendarDateRange.end_date,
            } as SearchCriteriaType;

            // Clean up undefined/null values but keep date range
            const cleanedCriteria = { ...criteria };
            Object.keys(cleanedCriteria).forEach(key => {
              const value = cleanedCriteria[key as keyof typeof cleanedCriteria];
              if (key !== 'start_date' && key !== 'end_date' && key !== 'type') {
                if (value === undefined || value === null || value === '') {
                  delete cleanedCriteria[key as keyof typeof cleanedCriteria];
                }
                // Remove empty arrays
                if (Array.isArray(value) && value.length === 0) {
                  delete cleanedCriteria[key as keyof typeof cleanedCriteria];
                }
              }
            });

            setCalendarSearchCriteria(cleanedCriteria);
          }
        }}
        filterDisplay={filterDisplay}
        setInputValue={setInputValue}
        onReset={() => {
          setFilterData({});
          if (viewMode === "calendar") {
            const currentMonthStart = format(startOfMonth(currentDate), "yyyy-MM-dd");
            const currentMonthEnd = format(endOfMonth(currentDate), "yyyy-MM-dd");
            setCalendarDateRange({
              start_date: currentMonthStart,
              end_date: currentMonthEnd,
            });
            setCalendarSearchCriteria({
              ...calendarInitialValue,
              start_date: currentMonthStart,
              end_date: currentMonthEnd,
            });
          } else {
            const resetCriteria = { ...gridInitialValue };
            setGridSearchCriteria(resetCriteria);
            setGridSearchCriteriaFromHook(resetCriteria as any);
          }
        }}
      />



      <BookingRequestDetails
        isOpen={isViewBookingOpen}
        onClose={() => setIsViewBookingOpen(false)}
        data={formData as FacilityBooking}
        refetch={refetch}
      />
    </div>
  );
}